import { type FC, useState } from "react";
import { Check } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { usePricingViewTracking } from "@/shared/hooks/usePricingViewTracking";
import { FREE_DOCUMENT_LIMIT } from "@/shared/hooks/useUploadPaywall";
import { sentryUtils } from "@/shared/utils/sentry";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import documentIllustration from "@/shared/assets/illustrations/document.svg";

interface DocumentLimitPaywallProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  returnPath?: string;
}

const FEATURES = [
  "Unlimited document uploads",
  "Probate guides for your county",
  "One organized estate inventory",
  "A 1-hour Estate Expert session",
];

export const DocumentLimitPaywall: FC<DocumentLimitPaywallProps> = ({
  isOpen,
  onOpenChange,
  returnPath,
}) => {
  const { user, person } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [showFullPaywall, setShowFullPaywall] = useState(false);
  const { trackDismissed } = usePricingViewTracking({
    isOpen,
    promptLocation: "document_limit",
    estateId: person?.estate_id,
  });

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      trackDismissed();
    }
    onOpenChange(open);
  };

  // TODO: update to open Stripe checkout for $500 Estate Setup tier when billing updates are in
  const handleCheckout = async () => {
    setIsLoading(true);
    try {
      const currentHost = window.location.origin;
      const fullReturnPath = returnPath
        ? `${currentHost}${returnPath}`
        : `${currentHost}${window.location.pathname}`;
      const fullCancelPath = `${currentHost}${window.location.pathname}`;

      const { data, error } = await supabase.functions.invoke("billing/checkout", {
        body: {
          returnUrl: fullReturnPath,
          cancelUrl: fullCancelPath,
          userId: user?.user_id,
          estateId: person?.estate_id,
          context: "DOCUMENT_LIMIT_PAYWALL",
          promptLocation: "document_limit",
          sourcePath: window.location.pathname,
          createCustomer: true,
          mode: "payment",
        },
      });

      if (error) {
        setIsLoading(false);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Checkout error:", error);
      sentryUtils.captureError(error, "DocumentLimitPaywall checkout failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle className="sr-only">Document upload limit reached</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <img src={documentIllustration} alt="Documents" className="h-24 w-24" />
            <div className="text-center">
              <h4>You've used all {FREE_DOCUMENT_LIMIT} free uploads</h4>
              <p className="mt-1 text-sm text-secondary">
                Estate Setup gives you unlimited uploads, so every statement, will, and record stays
                with the estate.
              </p>
            </div>

            <div className="flex w-full flex-col items-start gap-3 rounded-lg border-2 border-brand bg-white p-4">
              <div className="flex w-full items-center justify-between">
                <p className="font-medium">Estate Setup</p>
                <p className="font-medium">$500</p>
              </div>
              <div className="flex flex-col gap-2">
                {FEATURES.map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <Check className="h-4 w-4 shrink-0 text-brand" />
                    <p className="text-sm">{feature}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex w-full flex-col items-center gap-2">
              <Button
                variant="brand"
                className="w-full"
                disabled={isLoading}
                onClick={handleCheckout}
              >
                {isLoading ? "Loading..." : "Get Estate Setup"}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  onOpenChange(false);
                  setShowFullPaywall(true);
                }}
              >
                Compare all plans
              </Button>
            </div>

            <p className="text-2xs text-tertiary">
              Pay from the estate account or get reimbursed from estate funds.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <PostLossPaywall
        isOpen={showFullPaywall}
        onOpenChange={setShowFullPaywall}
        promptLocation="document_limit_full"
        returnPath={returnPath}
      />
    </>
  );
};
